import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box,
    Button,
    TextField,
    Typography,
    Container,
    Alert,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Menu,
    MenuItem,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    InputLabel,
    Select,
    Chip,
    InputAdornment,
    Checkbox,
    FormControlLabel,
    Snackbar,
    Divider,
    Tooltip,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import EditIcon from '@mui/icons-material/Edit';
import LockResetIcon from '@mui/icons-material/LockReset';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import HomeWorkOutlinedIcon from '@mui/icons-material/HomeWorkOutlined';
import { supabase } from '../services/supabaseClient';
import { useAuth } from '../components/AuthContext';

interface UserItem {
    id: string;
    email: string;
    user_metadata?: {
        name?: string;
    };
    app_metadata: {
        role?: string;
    };
    created_at: string;
}

interface ObraSimple {
    id: number;
    nome: string;
}

const isRoleAdmin = (role?: string) => role === 'Administrador' || role === 'admin';

const getRoleLabel = (role?: string) => {
    if (!role || role === 'user') return 'Usuário Padrão';
    if (role === 'admin') return 'Administrador';
    return role;
};

const getRoleChipColor = (role?: string): 'primary' | 'secondary' | 'success' | 'warning' | 'info' | 'default' => {
    const normalized = getRoleLabel(role);
    if (normalized === 'Administrador') return 'primary';
    if (normalized === 'Financeiro') return 'secondary';
    return 'default';
};

const AdminPage: React.FC = () => {
    const { user: currentUser, loading: authLoading } = useAuth();

    // Dados principais
    const [users, setUsers] = useState<UserItem[]>([]);
    const [usersLoading, setUsersLoading] = useState(true);
    const [usersError, setUsersError] = useState<string | null>(null);

    const [perfis, setPerfis] = useState<{ id: string; nome: string }[]>([]);
    const [perfisLoading, setPerfisLoading] = useState(true);

    const [obras, setObras] = useState<ObraSimple[]>([]);
    // Mapa user_id -> array de obra_id
    const [userObrasMap, setUserObrasMap] = useState<Record<string, number[]>>({});

    // Busca e Filtros da listagem
    const [searchTerm, setSearchTerm] = useState('');
    const [roleFilter, setRoleFilter] = useState('Todos');

    // Feedback global (Snackbar)
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false,
        message: '',
        severity: 'success',
    });

    const showSnackbar = (message: string, severity: 'success' | 'error' = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    // Modal de Criação de Usuário
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [newRole, setNewRole] = useState('');
    const [newSelectedObras, setNewSelectedObras] = useState<number[]>([]);
    const [createError, setCreateError] = useState<string | null>(null);
    const [createLoading, setCreateLoading] = useState(false);

    // Menu de ações da linha
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedUser, setSelectedUser] = useState<UserItem | null>(null);

    // Modal de Edição de Usuário
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editLoading, setEditLoading] = useState(false);
    const [editError, setEditError] = useState<string | null>(null);
    const [editName, setEditName] = useState('');
    const [editRole, setEditRole] = useState('');
    const [editSelectedObras, setEditSelectedObras] = useState<number[]>([]);

    // Modais de Confirmação (Excluir e Resetar Senha)
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const [resetDialogOpen, setResetDialogOpen] = useState(false);
    const [resetLoading, setResetLoading] = useState(false);

    const fetchObrasAndAssignments = useCallback(async () => {
        try {
            const [{ data: obrasData }, { data: assignmentsData }] = await Promise.all([
                supabase.from('obras').select('id, nome').order('nome'),
                supabase.from('obra_usuarios').select('obra_id, user_id'),
            ]);

            if (obrasData) {
                setObras(obrasData);
            }

            const map: Record<string, number[]> = {};
            (assignmentsData || []).forEach((row: any) => {
                if (!map[row.user_id]) map[row.user_id] = [];
                map[row.user_id].push(Number(row.obra_id));
            });
            setUserObrasMap(map);
        } catch (err) {
            console.error('Erro ao buscar obras e atribuições:', err);
        }
    }, []);

    const fetchUsers = useCallback(async () => {
        setUsersLoading(true);
        setUsersError(null);
        try {
            const { data, error } = await supabase.functions.invoke('list-users');
            if (error) throw error;
            if (data.error) throw new Error(data.error);
            const fetchedUsers = data.users || [];
            setUsers(fetchedUsers);
            await fetchObrasAndAssignments();
            return fetchedUsers;
        } catch (err: any) {
            console.error('fetchUsers catch error:', err);
            setUsersError(err.message || 'Falha ao carregar usuários.');
            return [];
        } finally {
            setUsersLoading(false);
        }
    }, [fetchObrasAndAssignments]);

    useEffect(() => {
        const fetchPerfis = async () => {
            setPerfisLoading(true);
            try {
                const { data, error } = await supabase.rpc('get_perfis');
                if (error) throw error;
                setPerfis(data || []);
                const defaultPerfil = data?.find((p: any) => p.nome === 'Usuário Padrão') || data?.[0];
                if (defaultPerfil) {
                    setNewRole(defaultPerfil.nome);
                }
            } catch (err: any) {
                console.error(err.message || 'Erro ao carregar perfis.');
            } finally {
                setPerfisLoading(false);
            }
        };
        fetchPerfis();
    }, []);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const obraNameMap = useMemo(() => {
        const map: Record<number, string> = {};
        obras.forEach((o) => {
            map[o.id] = o.nome;
        });
        return map;
    }, [obras]);

    const filteredUsers = useMemo(() => {
        const q = searchTerm.trim().toLowerCase();
        return users.filter((u) => {
            const roleLabel = getRoleLabel(u.app_metadata?.role);
            if (roleFilter !== 'Todos' && roleLabel !== roleFilter) {
                return false;
            }
            if (q) {
                const matchName = (u.user_metadata?.name || '').toLowerCase().includes(q);
                const matchEmail = (u.email || '').toLowerCase().includes(q);
                if (!matchName && !matchEmail) return false;
            }
            return true;
        });
    }, [users, searchTerm, roleFilter]);

    const syncUserObras = async (userId: string, targetRole: string, selectedObraIds: number[]) => {
        // Se for Administrador, tem acesso a todas as obras automaticamente; limpamos vínculos avulsos ou mantemos limpo
        await supabase.from('obra_usuarios').delete().eq('user_id', userId);

        if (!isRoleAdmin(targetRole) && selectedObraIds.length > 0) {
            const rows = selectedObraIds.map((obra_id) => ({
                obra_id,
                user_id: userId,
            }));
            const { error } = await supabase.from('obra_usuarios').insert(rows);
            if (error) throw error;
        }
    };

    const handleOpenCreateModal = () => {
        setName('');
        setEmail('');
        setPassword('');
        setShowPassword(false);
        setCreateError(null);
        setNewSelectedObras([]);
        const defaultPerfil = perfis.find((p) => p.nome === 'Usuário Padrão') || perfis[0];
        if (defaultPerfil) setNewRole(defaultPerfil.nome);
        setCreateDialogOpen(true);
    };

    const handleCreateUser = async (e: React.FormEvent) => {
        e.preventDefault();
        setCreateError(null);

        if (!name.trim() || !email.trim() || !password) {
            setCreateError('Preencha Nome, E-mail e Senha Provisória.');
            return;
        }
        if (password.length < 6) {
            setCreateError('A senha provisória deve ter pelo menos 6 caracteres.');
            return;
        }

        setCreateLoading(true);
        try {
            const { data, error } = await supabase.functions.invoke('create-user', {
                body: { email: email.trim(), password, role: newRole, name: name.trim() },
            });
            if (error) throw error;
            if (data?.error) throw new Error(data.error);

            const createdUserId = data?.user?.id;
            if (createdUserId && !isRoleAdmin(newRole) && newSelectedObras.length > 0) {
                await syncUserObras(createdUserId, newRole, newSelectedObras);
            }

            setCreateDialogOpen(false);
            showSnackbar(`Usuário ${email.trim()} criado com sucesso!`, 'success');
            fetchUsers();
        } catch (err: any) {
            setCreateError(err.message || 'Ocorreu um erro ao criar o usuário.');
        } finally {
            setCreateLoading(false);
        }
    };

    const handleMenuClick = (event: React.MouseEvent<HTMLElement>, user: UserItem) => {
        setAnchorEl(event.currentTarget);
        setSelectedUser(user);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleEditClick = () => {
        if (selectedUser) {
            setEditName(selectedUser.user_metadata?.name || '');
            setEditRole(getRoleLabel(selectedUser.app_metadata?.role));
            setEditSelectedObras(userObrasMap[selectedUser.id] || []);
            setEditError(null);
            setEditDialogOpen(true);
        }
        handleMenuClose();
    };

    const handleUpdateUser = async () => {
        if (!selectedUser) return;
        setEditError(null);

        if (selectedUser.id === currentUser?.id && !isRoleAdmin(editRole)) {
            setEditError('Você não pode remover o seu próprio perfil de Administrador.');
            return;
        }

        setEditLoading(true);
        try {
            const { data, error } = await supabase.functions.invoke('update-user', {
                body: {
                    target_user_id: selectedUser.id,
                    name: editName.trim(),
                    role: editRole,
                },
            });

            if (error) throw error;
            if (data?.error) throw new Error(data.error);

            await syncUserObras(selectedUser.id, editRole, editSelectedObras);

            setEditDialogOpen(false);
            setSelectedUser(null);
            showSnackbar('Usuário atualizado com sucesso!', 'success');
            fetchUsers();
        } catch (error: any) {
            console.error('Falha ao atualizar usuário:', error.message);
            setEditError(error.message || 'Erro ao atualizar usuário.');
        } finally {
            setEditLoading(false);
        }
    };

    const handleDeleteUser = async () => {
        if (!selectedUser) return;
        if (selectedUser.id === currentUser?.id) {
            showSnackbar('Você não pode excluir a sua própria conta.', 'error');
            setDeleteDialogOpen(false);
            return;
        }

        setDeleteLoading(true);
        try {
            // Limpar vínculos de obra antes de excluir o usuário no Auth
            await supabase.from('obra_usuarios').delete().eq('user_id', selectedUser.id);

            const { data, error: invokeError } = await supabase.functions.invoke('delete-user', {
                body: { target_user_id: selectedUser.id },
            });
            if (invokeError) throw invokeError;
            if (data?.error) throw new Error(data.error);

            showSnackbar(`Usuário ${selectedUser.email} excluído com sucesso.`, 'success');
            fetchUsers();
        } catch (error: any) {
            console.error('Falha ao excluir usuário:', error.message);
            showSnackbar(error.message || 'Erro ao excluir usuário.', 'error');
        } finally {
            setDeleteLoading(false);
            setDeleteDialogOpen(false);
            setSelectedUser(null);
        }
    };

    const handlePasswordReset = async () => {
        if (!selectedUser) return;
        setResetLoading(true);
        try {
            const { data, error } = await supabase.functions.invoke('send-password-reset', {
                body: { email: selectedUser.email },
            });

            if (error) {
                let errorMessage = error.message;
                if (error.context && typeof error.context.json === 'function') {
                    try {
                        const errorBody = await error.context.json();
                        errorMessage = errorBody.error || error.message;
                    } catch (e) {}
                }
                throw new Error(errorMessage);
            }

            if (data?.error) throw new Error(data.error);

            showSnackbar(`E-mail de recuperação de senha enviado para ${selectedUser.email}!`, 'success');
        } catch (error: any) {
            console.error('Falha ao enviar email de recuperação:', error);
            showSnackbar(`Erro: ${error.message || 'Falha ao enviar e-mail de recuperação'}`, 'error');
        } finally {
            setResetLoading(false);
            setResetDialogOpen(false);
            setSelectedUser(null);
        }
    };

    const toggleObraSelection = (
        obraId: number,
        currentList: number[],
        setter: React.Dispatch<React.SetStateAction<number[]>>
    ) => {
        setter(
            currentList.includes(obraId)
                ? currentList.filter((id) => id !== obraId)
                : [...currentList, obraId]
        );
    };

    if (authLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    const isSelfSelected = selectedUser?.id === currentUser?.id;

    return (
        <Container component="main" maxWidth="lg">
            <Box sx={{ my: 4 }}>
                {/* Cabeçalho */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: 2,
                        mb: 3,
                    }}
                >
                    <Box>
                        <Typography component="h1" variant="h4" sx={{ fontWeight: 700 }}>
                            Gerenciar Usuários
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Controle contas de acesso, perfis de permissão e obras atribuídas a cada usuário
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleOpenCreateModal}
                        sx={{ fontWeight: 600, px: 2.5, py: 1, borderRadius: 2 }}
                    >
                        Novo Usuário
                    </Button>
                </Box>

                {/* Barra de Busca e Filtro */}
                <Paper
                    elevation={0}
                    sx={{
                        p: 2,
                        mb: 3,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 2,
                        alignItems: 'center',
                    }}
                >
                    <TextField
                        size="small"
                        placeholder="Buscar por nome ou e-mail..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{ flexGrow: 1, minWidth: { xs: '100%', sm: 260 } }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" color="action" />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 200 } }}>
                        <InputLabel>Filtrar por Perfil</InputLabel>
                        <Select
                            value={roleFilter}
                            label="Filtrar por Perfil"
                            onChange={(e) => setRoleFilter(e.target.value)}
                        >
                            <MenuItem value="Todos">Todos os Perfis</MenuItem>
                            {perfis.map((p) => (
                                <MenuItem key={p.id} value={p.nome}>
                                    {p.nome}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Chip
                        label={`${filteredUsers.length} ${filteredUsers.length === 1 ? 'usuário' : 'usuários'}`}
                        variant="outlined"
                        sx={{ fontWeight: 600 }}
                    />
                </Paper>

                {/* Tabela de Usuários */}
                {usersLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 6 }}>
                        <CircularProgress />
                    </Box>
                ) : usersError ? (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {usersError}
                    </Alert>
                ) : (
                    <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700 }}>Nome</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>E-mail</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Perfil</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Obras com Acesso</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Criado em</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                                        Ações
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredUsers.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                            <Typography color="text.secondary">
                                                Nenhum usuário encontrado para os filtros informados.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    filteredUsers.map((u) => {
                                        const roleLabel = getRoleLabel(u.app_metadata?.role);
                                        const userIsAdmin = isRoleAdmin(roleLabel);
                                        const assignedObraIds = userObrasMap[u.id] || [];
                                        const isCurrentUser = u.id === currentUser?.id;

                                        return (
                                            <TableRow key={u.id} hover>
                                                <TableCell sx={{ fontWeight: 500 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        {u.user_metadata?.name || '-'}
                                                        {isCurrentUser && (
                                                            <Chip label="Você" size="small" color="info" variant="outlined" />
                                                        )}
                                                    </Box>
                                                </TableCell>
                                                <TableCell>{u.email}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={roleLabel}
                                                        color={getRoleChipColor(roleLabel)}
                                                        size="small"
                                                        sx={{ fontWeight: 600 }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {userIsAdmin ? (
                                                        <Chip
                                                            icon={<HomeWorkOutlinedIcon />}
                                                            label="Todas as Obras (Admin)"
                                                            size="small"
                                                            color="success"
                                                            variant="outlined"
                                                        />
                                                    ) : assignedObraIds.length === 0 ? (
                                                        <Typography variant="caption" color="text.disabled">
                                                            Nenhuma obra atribuída
                                                        </Typography>
                                                    ) : (
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 320 }}>
                                                            {assignedObraIds.slice(0, 3).map((obraId) => (
                                                                <Chip
                                                                    key={obraId}
                                                                    label={obraNameMap[obraId] || `Obra #${obraId}`}
                                                                    size="small"
                                                                    variant="outlined"
                                                                />
                                                            ))}
                                                            {assignedObraIds.length > 3 && (
                                                                <Tooltip
                                                                    title={assignedObraIds
                                                                        .slice(3)
                                                                        .map((id) => obraNameMap[id] || `Obra #${id}`)
                                                                        .join(', ')}
                                                                >
                                                                    <Chip
                                                                        label={`+${assignedObraIds.length - 3}`}
                                                                        size="small"
                                                                        color="primary"
                                                                    />
                                                                </Tooltip>
                                                            )}
                                                        </Box>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {new Date(u.created_at).toLocaleDateString('pt-BR')}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <IconButton onClick={(e) => handleMenuClick(e, u)}>
                                                        <MoreVertIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Box>

            {/* Menu de Ações */}
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                <MenuItem onClick={handleEditClick}>
                    <EditIcon fontSize="small" sx={{ mr: 1 }} /> Editar Usuário / Obras
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        handleMenuClose();
                        setResetDialogOpen(true);
                    }}
                >
                    <LockResetIcon fontSize="small" sx={{ mr: 1 }} /> Enviar Recuperação de Senha
                </MenuItem>
                <MenuItem
                    disabled={isSelfSelected}
                    onClick={() => {
                        handleMenuClose();
                        setDeleteDialogOpen(true);
                    }}
                    sx={{ color: 'error.main' }}
                >
                    <DeleteOutlineIcon fontSize="small" sx={{ mr: 1 }} /> Excluir Usuário
                </MenuItem>
            </Menu>

            {/* Modal de Criar Novo Usuário */}
            <Dialog
                open={createDialogOpen}
                onClose={createLoading ? undefined : () => setCreateDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Criar Novo Usuário</DialogTitle>
                <DialogContent>
                    <Box component="form" id="create-user-form" onSubmit={handleCreateUser} noValidate sx={{ pt: 1 }}>
                        {createError && (
                            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCreateError(null)}>
                                {createError}
                            </Alert>
                        )}
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            label="Nome do Usuário"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                        />
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            label="E-mail do Usuário"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                        />
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            label="Senha Provisória"
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                            size="small"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <FormControl fullWidth margin="normal">
                            <InputLabel>Perfil de Acesso</InputLabel>
                            <Select
                                value={newRole}
                                label="Perfil de Acesso"
                                onChange={(e) => setNewRole(e.target.value as string)}
                                disabled={perfisLoading}
                            >
                                {perfis.map((perfil) => (
                                    <MenuItem key={perfil.id} value={perfil.nome}>
                                        {perfil.nome}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {!isRoleAdmin(newRole) && obras.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                                <Divider sx={{ mb: 1.5 }} />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                        Obras com Acesso Permitido ({newSelectedObras.length}/{obras.length})
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Button
                                            size="small"
                                            onClick={() => setNewSelectedObras(obras.map((o) => o.id))}
                                        >
                                            Todas
                                        </Button>
                                        <Button size="small" color="inherit" onClick={() => setNewSelectedObras([])}>
                                            Limpar
                                        </Button>
                                    </Box>
                                </Box>
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        p: 1.5,
                                        maxHeight: 200,
                                        overflowY: 'auto',
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                                    }}
                                >
                                    {obras.map((obra) => (
                                        <FormControlLabel
                                            key={obra.id}
                                            control={
                                                <Checkbox
                                                    size="small"
                                                    checked={newSelectedObras.includes(obra.id)}
                                                    onChange={() =>
                                                        toggleObraSelection(obra.id, newSelectedObras, setNewSelectedObras)
                                                    }
                                                />
                                            }
                                            label={<Typography variant="body2">{obra.nome}</Typography>}
                                        />
                                    ))}
                                </Paper>
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateDialogOpen(false)} disabled={createLoading}>
                        Cancelar
                    </Button>
                    <Button type="submit" form="create-user-form" variant="contained" disabled={createLoading}>
                        {createLoading ? 'Criando...' : 'Criar Usuário'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Modal de Edição de Usuário */}
            <Dialog
                open={editDialogOpen}
                onClose={editLoading ? undefined : () => setEditDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Editar Usuário</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 1 }}>
                        {editError && (
                            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setEditError(null)}>
                                {editError}
                            </Alert>
                        )}
                        <TextField
                            margin="normal"
                            label="E-mail"
                            fullWidth
                            value={selectedUser?.email || ''}
                            disabled
                        />
                        <TextField
                            autoFocus
                            margin="normal"
                            label="Nome"
                            type="text"
                            fullWidth
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                        />
                        <FormControl fullWidth margin="normal">
                            <InputLabel>Perfil de Acesso</InputLabel>
                            <Select
                                value={editRole}
                                label="Perfil de Acesso"
                                onChange={(e) => setEditRole(e.target.value)}
                                disabled={isSelfSelected}
                            >
                                {perfis.map((perfil) => (
                                    <MenuItem key={perfil.id} value={perfil.nome}>
                                        {perfil.nome}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {isRoleAdmin(editRole) ? (
                            <Alert severity="info" sx={{ mt: 2 }}>
                                Usuários com perfil <strong>Administrador</strong> possuem acesso automático a todas as obras.
                            </Alert>
                        ) : (
                            obras.length > 0 && (
                                <Box sx={{ mt: 2 }}>
                                    <Divider sx={{ mb: 1.5 }} />
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                            Obras com Acesso Permitido ({editSelectedObras.length}/{obras.length})
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Button
                                                size="small"
                                                onClick={() => setEditSelectedObras(obras.map((o) => o.id))}
                                            >
                                                Todas
                                            </Button>
                                            <Button size="small" color="inherit" onClick={() => setEditSelectedObras([])}>
                                                Limpar
                                            </Button>
                                        </Box>
                                    </Box>
                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            p: 1.5,
                                            maxHeight: 220,
                                            overflowY: 'auto',
                                            display: 'grid',
                                            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                                        }}
                                    >
                                        {obras.map((obra) => (
                                            <FormControlLabel
                                                key={obra.id}
                                                control={
                                                    <Checkbox
                                                        size="small"
                                                        checked={editSelectedObras.includes(obra.id)}
                                                        onChange={() =>
                                                            toggleObraSelection(obra.id, editSelectedObras, setEditSelectedObras)
                                                        }
                                                    />
                                                }
                                                label={<Typography variant="body2">{obra.nome}</Typography>}
                                            />
                                        ))}
                                    </Paper>
                                </Box>
                            )
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDialogOpen(false)} disabled={editLoading}>
                        Cancelar
                    </Button>
                    <Button onClick={handleUpdateUser} variant="contained" disabled={editLoading}>
                        {editLoading ? 'Salvando...' : 'Salvar Alterações'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Diálogo de Confirmação de Exclusão */}
            <Dialog open={deleteDialogOpen} onClose={deleteLoading ? undefined : () => setDeleteDialogOpen(false)}>
                <DialogTitle>Confirmar Exclusão</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Tem certeza que deseja excluir o usuário <strong>{selectedUser?.email}</strong>? Esta ação é irreversível.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>
                        Cancelar
                    </Button>
                    <Button onClick={handleDeleteUser} color="error" variant="contained" disabled={deleteLoading}>
                        {deleteLoading ? 'Excluindo...' : 'Excluir'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Diálogo de Confirmação de Recuperação de Senha */}
            <Dialog open={resetDialogOpen} onClose={resetLoading ? undefined : () => setResetDialogOpen(false)}>
                <DialogTitle>Confirmar Recuperação de Senha</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Deseja enviar um e-mail de recuperação de senha para <strong>{selectedUser?.email}</strong>?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setResetDialogOpen(false)} disabled={resetLoading}>
                        Cancelar
                    </Button>
                    <Button onClick={handlePasswordReset} color="primary" variant="contained" disabled={resetLoading}>
                        {resetLoading ? 'Enviando...' : 'Enviar E-mail'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={5000}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default AdminPage;
